import Div from '@ui/layout/Div'
import Form from '@ui/elements/Form'
import Alert from '@ui/elements/Alert'
import Modal from '@ui/component/Modal'
import Buttons from '@ui/elements/Buttons'
import GeocodeAddress from "@shared/GeocodeAddress"
import sterilizationCompanyCallFormFields, { sterilizationCompanyCallSaveUrl } from "@form/sterilizationCompanyCallFormFields"
import { useDispatch, useSelector } from "react-redux"
import useStoreUpdate from "@hook/useStoreUpdate"
import useToggle from "@hook/useToggle"
import useMainData from '@hook/useMainData'
import useFormData from "@hook/useFormData"
import useCostCalculator from "@hook/useCostCalculator"
import useGoogleRecaptchaTokenizer from "@hook/useGoogleRecaptchaTokenizer"
import { updateSterilizationCompanyCallFormObject, updateSterilizationPayment } from "@store/slice/sterilizationSlice"
import { post } from '@util/xhrAPI'
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { func } from 'prop-types'
import { useNavigate } from 'react-router'
import { ROLE_PUBLIC } from "@router/Routes"

const CompanySterilizationCallForm = ({ nextStep }) => {
  const { sterilizationCompanyCallFormObject, errors } = useSelector(state => state.sterilization)
  const { data, mission, missionBillings } = useSelector(state => state.main)
  const { paymentConfig } = mission
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateSterilizationCompanyCallFormObject, sterilizationCompanyCallFormObject)
  const [isLoading, toggleLoading] = useToggle(false)
  const [userCoordinates, setUserCoordinates] = useState(null)
  const [t] = useTranslation('sterilization')
  const renderFormData = useFormData()
  const loadData = useMainData()
  const [currentAddress, setCurrentAddress] = useState(null)
  const [cost, setCost] = useState(null)
  const [unitsToBill, setUnitsToBill] = useState(null)
  const [currentTarget, setTarget] = useState(null)
  const calculateCost = useCostCalculator()
  const [isModalActive, toggleModal] = useToggle(false)
  const { callType } = sterilizationCompanyCallFormObject
  const [invokeChallenge, appendScript] = useGoogleRecaptchaTokenizer()

  const isSurfaceBased = () => {
    const billingType = data.billingTypes.filter(billingType => billingType.value === paymentConfig[`${callType}Payment`]?.billingType)[0]
    return billingType?.isSurfaceBased
  }

  const checkMyAddress = (validateAddress = false) => {
    toggleModal()
    if (true === validateAddress) {
      updateFormObjectProp('addressValidated', true)
    }
    if (false === validateAddress) {
      dispatch(updateSterilizationCompanyCallFormObject({
        ...sterilizationCompanyCallFormObject,
        'coordinates': null,
        sterilizationDistrict: '',
        sterilizationZipCode: '',
        sterilizationAddress: ''
      }))
    }
  }

  const renderCost = () => {
    if (!cost || 1 !== parseInt(paymentConfig[`${callType}Payment`]?.isBilled)) return <></>
    return <>
      <Alert
        message={t('form.nests_number_help', { nestsNumber: sterilizationCompanyCallFormObject.nestsNumber, cost: cost })}
        type='is-info'
      />
    </>
  }

  const generateFormStep = step => {
    return sterilizationCompanyCallFormFields(
      step,
      sterilizationCompanyCallFormObject,
      updateFormObject,
      updateFormObjectProp,
      errors,
      saveForm,
      checkMyAddress,
      callType ? 1 === parseInt(paymentConfig[`${callType}Payment`]?.isBilled) : false,
      callType ? isSurfaceBased() : false,
      isLoading,
      t,
      data,
      cost,
      renderCost,
      mission
    )
  }

  const fetchInitialData = () => {
    const dataObject = {
      cities: 'cities',
      districts: 'districts',
      billingTypes: 'billingTypes'
    }
    loadData(dataObject)
  }

  const saveForm = async () => {
    toggleLoading()
    const token = await invokeChallenge(updateFormObjectProp)
    const callObject = { ...sterilizationCompanyCallFormObject, 'g-recaptcha-response': token }
    post(sterilizationCompanyCallSaveUrl, renderFormData(callObject, ['userCoordinates']), response => {
      const { code, sterilizationCall, sterilizationOrder, paymentModes, paymentPage, validationPage, errors, isToPay } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
        if (isToPay && sterilizationOrder && sterilizationOrder.billedAmount) {
          dispatch(updateSterilizationPayment({ sterilizationOrder, paymentModes, paymentPage }))
          navigate(ROLE_PUBLIC['paymentPage'])
          window.localStorage.setItem('sterilizationOrder', sterilizationOrder.reference)
        } else {
          if (validationPage) {
            dispatch(updateValidationPage(validationPage))
            navigate(ROLE_PUBLIC['publicValidation'])
          } else {
            dispatch(updateSterilizationCompanyCallFormObject(sterilizationCall))
            window.localStorage.setItem('lastCompanyCall', sterilizationCall.id)
            nextStep('buildings')
          }
        }

      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }

      toggleLoading()
    })
  }

  useEffect(() => {
    updateFormObjectProp('mission', mission.id)
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (window.innerWidth <= 768) { //get user location if on mobile
      navigator.geolocation.getCurrentPosition(function (position) {
        setUserCoordinates({ lat: position.coords.latitude, lng: position.coords.longitude })
      })
    }
    appendScript()
  }, [])

  useEffect(() => {
    if (userCoordinates) {
      updateFormObjectProp('userCoordinates', userCoordinates)
    }
  }, [userCoordinates])

  useEffect(() => {
    const { sterilizationDistrict, sterilizationZipCode, sterilizationAddress } = sterilizationCompanyCallFormObject
    if (sterilizationDistrict && sterilizationZipCode && sterilizationAddress) {
      const selectedDistrict = data.districts.filter(district => district.id != sterilizationCompanyCallFormObject.sterilizationDistrict)[0]
      const writtenAddress = `${selectedDistrict.label}+${sterilizationCompanyCallFormObject.sterilizationZipCode}+${sterilizationCompanyCallFormObject.sterilizationAddress}`
      setCurrentAddress(writtenAddress)
    }

    if (
      sterilizationCompanyCallFormObject.nestsNumber && (
        sterilizationCompanyCallFormObject.nestsNumber != unitsToBill || sterilizationCompanyCallFormObject.callType != currentTarget
      )
    ) {
      setCost(calculateCost(
        parseFloat(sterilizationCompanyCallFormObject.nestsNumber),
        Object.values(missionBillings),
        sterilizationCompanyCallFormObject.callType
      ))
      setTarget(sterilizationCompanyCallFormObject.callType)
      setUnitsToBill(sterilizationCompanyCallFormObject.nestsNumber)
    }

  }, [sterilizationCompanyCallFormObject])

  return (
    <>
      <Form cssClass='citizen-sterilization-call'>
        <Div cssClass='columns is-multiline'>
          {generateFormStep('call')}
        </Div>

        <Div cssClass='columns is-multiline'>
          <Div cssClass='column is-6 is-multiline'>
            {generateFormStep('establishmentResponsible')}
          </Div>
          <Div cssClass='column is-6 is-multiline'>
            {generateFormStep('callResponsible')}
          </Div>
        </Div>

        <Div cssClass='columns is-multiline'>
          {generateFormStep('callLocation')}
        </Div>
        {'company' === sterilizationCompanyCallFormObject.callType && <>
          <Div cssClass='columns is-multiline'>
            {generateFormStep('company')}
          </Div>
        </>}

        <Div cssClass='columns is-multiline'>
          {generateFormStep('callChecks')}
        </Div>
      </Form>
      <Modal cssClass='confirm-address' isActive={isModalActive} closeCallback={toggleModal}>
        <GeocodeAddress address={currentAddress} callback={updateFormObjectProp} />
        <Buttons buttons={[
          {
            icon: 'icon-done',
            text: t('common:actions.validate'),
            callback: () => checkMyAddress(true),
            cssClass: 'is-success'
          },
          {
            icon: 'icon-keyboard_arrow_left',
            text: t('common:actions.cancel'),
            callback: () => checkMyAddress(false),
            cssClass: 'is-danger'
          }
        ]} />
      </Modal>
    </>
  )
}

CompanySterilizationCallForm.propTypes = {
  nextStep: func
}

export default CompanySterilizationCallForm
